import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrompt } from 'hooks/usePrompt';

import { SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

const OtomationRulesSortForm = NiceModal.create(({ id, modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					rules: yup.array().of(
						yup.object().shape({
							id: yup.string().nullable().required(),
							ruleName: yup.string().nullable().required(),
							priorityOrder: yup.number().nullable().required()
						})
					)
				})
				.required(),
		[]
	);
	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();

	const dispatch = useDispatch();

	// Form config
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			rules: [{ ruleName: null, priorityOrder: null, id: null }]
		}
	});

	const {
		handleSubmit,
		control,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const { fields, move } = useFieldArray({
		control,
		name: 'rules'
	});

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const columns = useMemo(
		() => [
			{
				header: 'order',
				id: 'order',
				size: isMobile ? 10 : 30,
				renderCell: ({ row: { index } }) => <Typography>{index + 1}</Typography>,
				showOnMobile: true
			},
			{
				id: 'ruleID',
				header: 'ruleID',
				accessor: 'ruleID',
				size: isMobile ? 40 : 120,
				renderCell: ({ row: { index } }) => <Typography noWrap>{watch(`rules[${index}].id`)}</Typography>,
				showOnMobile: false
			},
			{
				id: 'ruleName',
				header: 'ruleName',
				accessor: 'ruleName',
				size: isMobile ? 40 : 120,
				renderCell: ({ row: { index } }) => <Typography noWrap>{watch(`rules[${index}].ruleName`)}</Typography>,
				showOnMobile: true
			}
		],
		[isMobile]
	);

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		try {
			const { data, status } = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_AUTOMATION_ORDERS, {});

			if (status === 200) {
				reset({
					rules: data?.ruleOverviews?.map?.((item) => ({
						id: item.id,
						ruleName: item.name,
						priorityOrder: item.rank
					}))
				});
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
	}, [id]);

	useEffect(() => {
		fillForm();
	}, []);

	const onSubmit = async (data) => {
		setIsLoading(true);

		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.UPDATE_AUTOMATION_ORDERS, {
				ruleIds: data?.rules?.map?.((item) => item.id)
			});

			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.OTOMATION_RULES));
				modal.remove();
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal fullwidth maxWidth="medium" name={modalName} className="modal-orderOtomationSortForm">
			<ModalTitle>
				<Typography variant="p1">OrderOtomationSortForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="sortRules" style={{ width: 'inherit' }}>
						<Table loading={isLoading} data={fields} columns={columns} onRowDrag={move} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={() => modal.remove()} />
				<Button
					label="save"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OtomationRulesSortForm;
